import React, { useState, useEffect, useRef } from 'react';
import {
  Search, Plus, X, Check, Shield, Eye, EyeOff,
  LayoutDashboard, Users, CreditCard, Receipt, FileBarChart,
  Megaphone, Send, TrendingUp, CalendarDays, MessageCircle,
  ChevronDown, ChevronUp, Lock, Unlock, AlertTriangle, User,
  FileText, Clock, Upload, Download, Trash2, Edit2, History,
  FilePlus, FileCheck, FileX, CreditCard as CardIcon, ScrollText, Wallet, ClipboardCheck, FileSignature, Database
} from 'lucide-react';
import './Administracao.css';

// =============================================================================
// 1. Configuração da API (mesmo padrão usado nas outras páginas)
// =============================================================================
const API_BASE = '/api';
const getAuthHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
});

async function apiFetch(endpoint, options = {}) {
  const res = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: { ...getAuthHeaders(), ...options.headers },
  });
  
  // ✅ PRIMEIRO: Verifica se é 204 No Content (exclusão bem-sucedida)
  if (res.status === 204 || res.status === 205) {
    return null;
  }
  
  // ✅ SEGUNDO: Verifica se é erro
  if (!res.ok) {
    let errorMessage = 'Erro na requisição';
    try {
      const error = await res.json();
      errorMessage = error.error || errorMessage;
    } catch (e) {
      errorMessage = await res.text().catch(() => errorMessage);
    }
    throw new Error(errorMessage);
  }
  
  // ✅ TERCEIRO: Para respostas com conteúdo, tenta parsear JSON
  const text = await res.text();
  if (!text) return null;
  
  try {
    return JSON.parse(text);
  } catch (e) {
    console.warn('Resposta não é JSON válido:', text.substring(0, 100));
    return text;
  }
}

// =============================================================================
// 2. Módulos, cargos e permissões padrão (dados estáticos, como antes)
// =============================================================================
const ALL_MODULES = [
  { id: 'dashboard',      name: 'Dashboard',        icon: LayoutDashboard },
  { id: 'data-base',       name: 'Data Base',         icon: Database        },
  { id: 'clientes',       name: 'Clientes',          icon: Users           },
  { id: 'repasse-parceiro', name: 'Repasse Parceiro', icon: Wallet          },
  { id: 'consulta-uc',    name: 'Consulta de UC',   icon: Search          },
  { id: 'pagamentos',     name: 'Pagamentos',        icon: CreditCard      },
  { id: 'gerar-cobranca', name: 'Gerar Cobrança',    icon: Receipt         },
  { id: 'cobrancas',      name: 'Cobranças',         icon: Wallet          }, 
  { id: 'relatorios',     name: 'Relatórios',        icon: FileBarChart    },
  { id: 'marketing',      name: 'Marketing',         icon: Megaphone       },
  { id: 'envio',          name: 'Envio',             icon: Send            },
  { id: 'esteira-vendas', name: 'Esteira de Vendas', icon: TrendingUp      },
  { id: 'checklist',      name: 'Checklist',         icon: ClipboardCheck  },
  { id: 'agendamentos',   name: 'Agendamentos',      icon: CalendarDays    },
  { id: 'atendimento',    name: 'Atendimento',       icon: MessageCircle   },
  { id: 'administracao',  name: 'Administração',     icon: Shield          },
  { id: 'clicksign',       name: 'Envios ClickSign', icon: FileSignature },
{ id: 'suporte',         name: 'Suporte',           icon: MessageCircle },
];

const ALL_PERMS = ALL_MODULES.map(m => m.id);
const ROLES = ['Admin', 'Gestor', 'Atendente', 'Financeiro', 'Vendedor', 'Pós Vendas', 'Marketing'];
// ── Partes de módulo (sub-opções do menu): quem tem o módulo inteiro tem todas as partes ──
const SUB_PERMS = [
  { id: 'clientes:consulta',     parent: 'clientes',  name: 'Consulta de Clientes' },
  { id: 'clientes:cadastro',     parent: 'clientes',  name: 'Cadastro de Clientes' },
  { id: 'checklist:medico',      parent: 'checklist', name: 'Médico' },
  { id: 'checklist:odontologia', parent: 'checklist', name: 'Odontologia' },
  { id: 'checklist:cartao',      parent: 'checklist', name: 'Cartão' },
  { id: 'agendamentos:medico',      parent: 'agendamentos', name: 'Médico' },
  { id: 'agendamentos:odontologia-agenda', parent: 'agendamentos', name: 'Odontologia' },
  { id: 'agendamentos:odontologia',       parent: 'agendamentos', name: 'Carteira Odonto' },
  { id: 'agendamentos:conferencia-valores-exames', parent: 'agendamentos', name: 'Conferência valores de exames' },
  { id: 'clicksign:contrato',    parent: 'clicksign', name: 'Contrato Adesão' },
  { id: 'clicksign:termo',       parent: 'clicksign', name: 'Termo de Cancelamento' },
  { id: 'clicksign:acompanhar',  parent: 'clicksign', name: 'Acompanhar Envios' },
{ id: 'suporte:asaas',     parent: 'suporte',   name: 'ASAAS' },
{ id: 'suporte:cpfl',      parent: 'suporte',   name: 'CPFL' },
{ id: 'suporte:pix-tools', parent: 'suporte',   name: 'PIX TOOLS' },
];
const COM_PARTES = ['clientes', 'checklist', 'agendamentos', 'clicksign', 'suporte'];
// Modalidades de pagamento (Cadastro de Clientes e Gerar Cobrança): sem nenhuma marcada, o usuário usa todas
const MODALIDADES = [
  { id: 'modalidade:cpfl',  name: 'CPFL' },
  { id: 'modalidade:asaas', name: 'Asaas' },
  { id: 'modalidade:pix',   name: 'Pix Santander' },
];
const partesDe = (modulo) => SUB_PERMS.filter((s) => s.parent === modulo);
// Partes que NÃO vêm junto com o módulo inteiro: precisam ser marcadas à parte e nunca são absorvidas.
const PERMS_INDEPENDENTES = ['agendamentos:conferencia-valores-exames'];
const partesHerdaveis = (modulo) => partesDe(modulo).filter((s) => !PERMS_INDEPENDENTES.includes(s.id));

// ── Setores de cada cargo e as telas padrão de cada par cargo + setor ──
const TEMPLATES = {
  Admin:  { 'Administração': ALL_PERMS },
  Gestor: { 'Administração': ALL_PERMS.filter((p) => p !== 'administracao') },
  Atendente: {
    'Odontológico': ['dashboard', 'consulta-uc', 'clientes', 'cobrancas', 'gerar-cobranca', 'checklist:odontologia', 'agendamentos:odontologia-agenda', 'agendamentos:odontologia', 'atendimento', 'clicksign:contrato', 'clicksign:acompanhar', 'modalidade:pix'],
    'Médico':       ['dashboard', 'consulta-uc', 'clientes:consulta', 'cobrancas', 'checklist:medico', 'agendamentos:medico', 'atendimento', 'clicksign:contrato', 'clicksign:acompanhar'],
    'Cartão':       ['dashboard', 'consulta-uc', 'clientes', 'cobrancas', 'gerar-cobranca', 'checklist:cartao', 'atendimento', 'clicksign'],
  },
  Financeiro:   { 'Finanças e Contabilidade': ['dashboard', 'cobrancas', 'pagamentos', 'relatorios'] },
  Vendedor:     { 'Vendas': ['dashboard', 'consulta-uc', 'esteira-vendas', 'atendimento', 'clicksign:contrato', 'clicksign:acompanhar'] },
  'Pós Vendas': { 'Vendas': ['dashboard', 'consulta-uc', 'esteira-vendas', 'atendimento', 'clicksign:contrato', 'clicksign:acompanhar'] }, // provisório: igual ao Vendedor
  Marketing:    { 'Chief Marketing Officer': ['dashboard', 'marketing', 'atendimento', 'checklist', 'agendamentos', 'clicksign'] },
};

function canonCargo(r) {
  return ROLES.find((x) => x.toLowerCase() === String(r || '').toLowerCase()) || r;
}
function ehAdmin(roles) {
  return (roles || []).some((r) => String(r).toLowerCase() === 'admin');
}
// cargo com um setor só já vem com ele marcado
function setoresAutomaticos(cargo) {
  const opcoes = Object.keys(TEMPLATES[cargo] || {});
  return opcoes.length === 1 ? opcoes : [];
}
// junta as telas: quem tem o módulo inteiro absorve as partes dele
function combinarPerms(lista) {
  const unicas = [...new Set(lista)];
  const completas = new Set(unicas.filter((p) => !p.includes(':')));
  return unicas.filter((p) => !p.includes(':') || PERMS_INDEPENDENTES.includes(p) || !completas.has(p.split(':')[0]));
}
// telas padrão de um usuário: soma dos padrões de cada cargo + setor escolhido
function permsPadrao(roles, setores) {
  const lista = [];
  let algumLivre = false;
  (roles || []).forEach((r) => {
    const cargo = canonCargo(r);
    const modelos = TEMPLATES[cargo] || {};
    const marcados = setores && setores[cargo] && setores[cargo].length ? setores[cargo] : setoresAutomaticos(cargo);
    marcados.forEach((s) => {
      const t = modelos[s];
      if (!t) return;
      lista.push(...t);
      // setor que usa Cadastro ou Cobrança SEM restrição de modalidade vale para todas as modalidades
      const usa = t.includes('clientes') || t.includes('clientes:cadastro') || t.includes('gerar-cobranca');
      if (usa && !t.some((p) => p.startsWith('modalidade:'))) algumLivre = true;
    });
  });
  const unica = combinarPerms(lista.length ? lista : ['dashboard']);
  return algumLivre ? unica.filter((p) => !p.startsWith('modalidade:')) : unica;
}
// padrão "por cargo" antigo (união dos setores), mantido por compatibilidade
const DEFAULT_PERMS = Object.fromEntries(
  Object.entries(TEMPLATES).map(([cargo, setores]) => [cargo, combinarPerms(Object.values(setores).flat())])
);

function estadoModulo(perms, id) {
  const lista = perms || [];
  if (lista.includes(id)) return 'completo';
  return lista.some((p) => p.startsWith(`${id}:`)) ? 'parcial' : 'nenhum';
}
function modulosComAcesso(perms) {
  return ALL_MODULES.filter((m) => estadoModulo(perms, m.id) !== 'nenhum');
}
function rotuloPerm(p) {
  const mod = ALL_MODULES.find((m) => m.id === p);
  if (mod) return { icon: mod.icon, nome: mod.name };
  const sub = SUB_PERMS.find((s) => s.id === p);
  if (!sub) {
    const modal = MODALIDADES.find((m) => m.id === p);
    return modal ? { icon: CreditCard, nome: `Modalidade · ${modal.name}` } : null;
  }
  const pai = ALL_MODULES.find((m) => m.id === sub.parent);
  return { icon: pai.icon, nome: `${pai.name} · ${sub.name}` };
}
function textoSetores(u) {
  return (u.roles || [])
    .map((r) => { const s = ((u.setores || {})[canonCargo(r)] || []); return s.length ? `${r}: ${s.join(', ')}` : null; })
    .filter(Boolean)
    .join(' · ');
}
const ESTEIRA_PERMS = {
  Admin:        { prevenda: true,  posvenda: true  },
  Vendedor:     { prevenda: true,  posvenda: false },
  'Pós Vendas': { prevenda: false, posvenda: true  },
};

function getEsteiraPerms(roles) {
  const arr = Array.isArray(roles) ? roles : [roles];
  return {
    prevenda: arr.some(r => ESTEIRA_PERMS[r]?.prevenda),
    posvenda: arr.some(r => ESTEIRA_PERMS[r]?.posvenda),
  };
}

function roleColor(roles) {
  const arr = Array.isArray(roles) ? roles : [roles];
  const primary = arr[0] || 'Admin';
  const colors = {
    Admin: '#4f46e5', 
    Gestor: '#0d9488',  // ← NOVO (teal/verde-azulado)
    Atendente: '#0891b2', 
    Financeiro: '#059669',
    Vendedor: '#d97706', 
    'Pós Vendas': '#7c3aed', 
    Marketing: '#db2777'
  };
  return colors[primary] || '#64748b';
}

// =============================================================================
// 3. Componentes auxiliares (Toast, Modal, etc.)
// =============================================================================
function Toast({ msg, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3000);
    return () => clearTimeout(t);
  }, [onClose]);
  return <div className="adm-toast"><Check size={14} /> {msg}</div>;
}

function Modal({ title, onClose, wide, children, footer, fecharAoClicarFora = true }) {
  return (
    <div className="adm-overlay" onClick={fecharAoClicarFora ? onClose : undefined}>
      <div className={`adm-modal ${wide ? 'adm-modal-wide' : ''}`} onClick={e => e.stopPropagation()}>
        <div className="adm-modal-header">
          <h3>{title}</h3>
          <button className="adm-modal-close" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="adm-modal-body">{children}</div>
        {footer && <div className="adm-modal-footer">{footer}</div>}
      </div>
    </div>
  );
}

// =============================================================================
// 4. Modal de Permissões (agora integrado)
// =============================================================================
function PermissoesModal({ user, onClose, onSave }) {
  const [perms, setPerms] = useState(user.permissions || []);
  const [avisoPadrao, setAvisoPadrao] = useState('');
  const admin = ehAdmin(user.roles);
  const liberados = modulosComAcesso(perms).length;

  const toggle = (id) => {
    if (admin && id === 'administracao') return;
    setPerms(prev => {
      const sem = prev.filter(p => p !== id && !(p.startsWith(`${id}:`) && !PERMS_INDEPENDENTES.includes(p)));
      return estadoModulo(prev, id) === 'completo' ? sem : [...sem, id];
    });
  };

  const toggleParte = (modulo, parteId) => {
    setPerms(prev => {
      // Parte independente: liga/desliga só ela, sem mexer no módulo.
      if (PERMS_INDEPENDENTES.includes(parteId)) {
        return prev.includes(parteId) ? prev.filter(p => p !== parteId) : [...prev, parteId];
      }
      const todas = partesHerdaveis(modulo).map(s => s.id);
      const atuais = prev.includes(modulo) ? todas : todas.filter(s => prev.includes(s));
      const novas = atuais.includes(parteId) ? atuais.filter(s => s !== parteId) : [...atuais, parteId];
      const resto = prev.filter(p => p !== modulo && !(p.startsWith(`${modulo}:`) && !PERMS_INDEPENDENTES.includes(p)));
      return novas.length === todas.length ? [...resto, modulo] : [...resto, ...novas];
    });
  };

  const toggleAll = () => {
    if (admin) {
      setPerms(['administracao']);
    } else {
      setPerms(liberados === ALL_MODULES.length ? [] : ALL_PERMS);
    }
  };

  const applyDef = () => {
    const cargos = (user.roles || []).map(canonCargo);
    const semSetor = cargos.filter(c => Object.keys(TEMPLATES[c] || {}).length > 1 && !((user.setores || {})[c] || []).length);
    if (semSetor.length) {
      setAvisoPadrao(`Defina o setor de ${semSetor.join(' e ')} em "Editar" para carregar o padrão.`);
      return;
    }
    setAvisoPadrao('');
    setPerms(permsPadrao(user.roles, user.setores));
  };

  return (
    <div className="adm-overlay" onClick={onClose}>
      <div className="adm-modal adm-modal-wide" onClick={e => e.stopPropagation()}>
        <div className="adm-modal-header">
          <div className="adm-modal-header-left">
            <div className="adm-perm-avatar">{user.name?.charAt(0) || 'U'}</div>
            <div>
              <div className="adm-perm-user-name">{user.name}</div>
              <div className="adm-perm-user-role">{user.roles?.join(' + ')} · {user.email}</div>
            </div>
          </div>
          <button className="adm-modal-close" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="adm-modal-body">
          <div className="adm-perm-intro"><Shield size={15} /><span>Selecione quais módulos este usuário pode acessar</span></div>
          <div className="adm-perm-quick">
            <button className="adm-quick-btn" onClick={toggleAll}>
              {liberados === ALL_MODULES.length ? <EyeOff size={13} /> : <Eye size={13} />}
              {liberados === ALL_MODULES.length ? 'Desmarcar tudo' : 'Marcar tudo'}
            </button>
            <button className="adm-quick-btn" onClick={applyDef}><User size={13} /> Padrão do cargo</button>
            <span className="adm-perm-count">{liberados} de {ALL_MODULES.length} módulos liberados</span>
          </div>
          {avisoPadrao && <div className="adm-perm-warning"><AlertTriangle size={13} /> {avisoPadrao}</div>}
          <div className="adm-perm-grid">
            {ALL_MODULES.map(mod => {
              const estado = estadoModulo(perms, mod.id);
              const active = estado !== 'nenhum';
              const locked = admin && mod.id === 'administracao';
              return (
                <div key={mod.id} className={`adm-perm-item ${active ? 'active' : ''} ${estado === 'parcial' ? 'parcial' : ''} ${locked ? 'locked' : ''}`} onClick={() => !locked && toggle(mod.id)}>
                  <div className={`adm-perm-icon ${active ? 'icon-on' : 'icon-off'}`}><mod.icon size={18} /></div>
                  <span className="adm-perm-name">{mod.name}</span>
                  <div className={`adm-perm-check ${active ? 'check-on' : ''} ${estado === 'parcial' ? 'check-parcial' : ''}`}>
                    {locked ? <Lock size={11} /> : estado === 'completo' ? <Check size={11} /> : estado === 'parcial' ? <span>½</span> : null}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="adm-perm-partes">
            <div className="adm-perm-partes-titulo">Liberar só uma parte do módulo</div>
            {COM_PARTES.map(modulo => {
              const mod = ALL_MODULES.find(m => m.id === modulo);
              const estado = estadoModulo(perms, modulo);
              return (
                <div key={modulo} className="adm-perm-parte-grupo">
                  <span className="adm-perm-parte-nome"><mod.icon size={13} /> {mod.name}</span>
                  {partesDe(modulo).map(s => {
                    const ativa = PERMS_INDEPENDENTES.includes(s.id) ? perms.includes(s.id) : (estado === 'completo' || perms.includes(s.id));
                    return (
                      <button key={s.id} type="button" className={`adm-parte-chip ${ativa ? 'active' : ''}`} onClick={() => toggleParte(modulo, s.id)}>
                        {ativa && <Check size={12} />} {s.name}
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
          <div className="adm-perm-partes">
            <div className="adm-perm-partes-titulo">Modalidades de pagamento permitidas (nenhuma marcada = todas)</div>
            <div className="adm-perm-parte-grupo">
              <span className="adm-perm-parte-nome"><CreditCard size={13} /> Cadastro e cobrança</span>
              {MODALIDADES.map(m => {
                const ativa = perms.includes(m.id);
                return (
                  <button key={m.id} type="button" className={`adm-parte-chip ${ativa ? 'active' : ''}`}
                    onClick={() => setPerms(prev => prev.includes(m.id) ? prev.filter(p => p !== m.id) : [...prev, m.id])}>
                    {ativa && <Check size={12} />} {m.name}
                  </button>
                );
              })}
            </div>
          </div>
          {admin && (
            <div className="adm-perm-warning"><AlertTriangle size={13} /> Administradores sempre têm acesso ao módulo de Administração.</div>
          )}
        </div>
        <div className="adm-modal-footer">
          <button className="adm-btn-cancel" onClick={onClose}>Cancelar</button>
          <button className="adm-btn-save" onClick={() => onSave(user.id, perms)}><Check size={14} /> Salvar Permissões</button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// 5. Modal de Histórico (busca da API)
// =============================================================================
const tipoConfig = {
  info:    { color: 'blue',   label: 'Consulta',  Icon: Eye      },
  criacao: { color: 'green',  label: 'Criação',   Icon: FilePlus },
  edicao:  { color: 'amber',  label: 'Edição',    Icon: Edit2    },
  exclusao: { color: 'red',   label: 'Exclusão',  Icon: Trash2   },
};

function HistoricoModal({ user, onClose }) {
  const [logs, setLogs] = useState([]);
  const [filter, setFilter] = useState('todos');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const data = await apiFetch(`/admin/users/${user.id}/history`);
        setLogs(data);
      } catch (err) {
        console.error('Erro ao carregar histórico:', err);
        setLogs([]);
      } finally {
        setLoading(false);
      }
    };
    fetchLogs();
  }, [user.id]);

  const filtered = filter === 'todos' ? logs : logs.filter(l => l.tipo === filter);

  const exportLogs = () => {
    const txt = filtered.map(l => `[${formatDateTime(l.hora)}] ${l.acao} — ${l.modulo}`).join('\n');
    navigator.clipboard.writeText(txt);
  };

 function formatDateTime(isoString) {
    if (!isoString) return '—';
    const dataNaoReconhecida = Number.isNaN(new Date(isoString).getTime());
    if (dataNaoReconhecida) {
      const textoData = String(isoString);
      return textoData === 'Invalid Date' ? '—' : textoData.replace(/(\d{2}:\d{2}):\d{2}$/, '$1');
    }
  if (!isoString) return '';
  const date = new Date(isoString);
  // Formata diretamente para o fuso de Brasília (UTC-3)
  return date.toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

  return (
    <div className="adm-overlay" onClick={onClose}>
      <div className="adm-modal adm-modal-wide" onClick={e => e.stopPropagation()}>
        <div className="adm-modal-header">
          <div className="adm-modal-header-left">
            <div className="adm-perm-avatar" style={{ background: roleColor(user.roles) }}>{user.name?.charAt(0)}</div>
            <div>
              <div className="adm-perm-user-name">Histórico — {user.name}</div>
              <div className="adm-perm-user-role">{user.roles?.join(' + ')} · {logs.length} registro(s)</div>
            </div>
          </div>
          <button className="adm-modal-close" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="adm-modal-body">
          <div className="hist-filters">
            {['todos','info','criacao','edicao','exclusao'].map(f => (
              <button key={f} className={`hist-filter-btn ${filter === f ? 'active-' + (f === 'todos' ? 'blue' : tipoConfig[f]?.color) : ''}`} onClick={() => setFilter(f)}>
                {f === 'todos' ? 'Todos' : tipoConfig[f]?.label}
                <span className="hist-filter-count">
                  {f === 'todos' ? logs.length : logs.filter(l => l.tipo === f).length}
                </span>
              </button>
            ))}
          </div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}>Carregando histórico...</div>
          ) : filtered.length === 0 ? (
            <div className="hist-empty"><History size={32} /><p>Nenhum registro encontrado</p></div>
          ) : (
            <div className="hist-timeline">
            {filtered.map(log => {
  const cfg = tipoConfig[log.tipo] || tipoConfig.info;
  return (
    <div key={log.id} className="hist-item">
      <div className={`hist-dot dot-${cfg.color}`}><cfg.Icon size={12} /></div>
      <div className="hist-content">
        <div className="hist-acao">{log.acao}</div>
        <div className="hist-meta">
          <span className={`hist-tipo tipo-${cfg.color}`}>{cfg.label}</span>
          <span className="hist-modulo">📂 {log.modulo}</span>
          <span className="hist-hora"><Clock size={11}/> {formatDateTime(log.hora)}</span>
        </div>
      </div>
    </div>
  );
})}
            </div>
          )}
        </div>
        <div className="adm-modal-footer">
          <button className="adm-btn-cancel" onClick={onClose}>Fechar</button>
          <button className="adm-btn-save" onClick={exportLogs}><Download size={14} /> Exportar Log</button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// 6. Modal de criação/edição de usuário (integrado)
// =============================================================================
function UserModal({ editingUser, onClose, onSave }) {
  const initRoles = editingUser?.roles || ['Atendente'];
  const initSetores = editingUser?.setores || {};
  const initPerms = editingUser?.permissions || permsPadrao(initRoles, initSetores);

  const [form, setForm] = useState({
    name: editingUser?.name || '',
    email: editingUser?.email || '',
    cpf: editingUser?.cpf || '',
    roles: initRoles,
    setores: initSetores,
    status: editingUser?.status || 'ativo',
    permissions: initPerms,
    password: '', // apenas para criação
  });
  const [errors, setErrors] = useState({});
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState(null);

  const setField = (k, v) => {
    setForm(p => ({ ...p, [k]: v }));
    if (errors[k]) setErrors(p => ({ ...p, [k]: null }));
  };

  const toggleRole = (role) => {
    setForm(p => {
      let next = p.roles.includes(role)
        ? p.roles.filter(r => r !== role)
        : [...p.roles, role];
      if (next.length === 0) next = [role];
      const cargo = canonCargo(role);
      const setores = { ...p.setores };
      if (next.includes(role) && !p.roles.includes(role)) setores[cargo] = setoresAutomaticos(cargo);
      if (!next.includes(role)) delete setores[cargo];
      return { ...p, roles: next, setores, permissions: permsPadrao(next, setores) };
    });
  };

  const toggleSetor = (cargo, setor) => {
    setForm(p => {
      const atual = p.setores[cargo] || [];
      const novos = atual.includes(setor) ? atual.filter(s => s !== setor) : [...atual, setor];
      const setores = { ...p.setores, [cargo]: novos };
      return { ...p, setores, permissions: permsPadrao(p.roles, setores) };
    });
    if (errors.setores) setErrors(p => ({ ...p, setores: null }));
  };

  const handleSave = () => {
    const e = {};
    if (!form.name) e.name = 'Nome obrigatório';
    if (!form.email) e.email = 'E-mail obrigatório';
    if (!editingUser && !form.password) {
      e.password = 'Senha obrigatória para novo usuário';
    }
    if (!editingUser) {
      const semSetor = form.roles.map(canonCargo).filter(c => Object.keys(TEMPLATES[c] || {}).length > 0 && !(form.setores[c] || []).length);
      if (semSetor.length) e.setores = `Escolha o setor de: ${semSetor.join(', ')}`;
    }
    if (Object.keys(e).length) { setErrors(e); return; }
    onSave(form);
  };

  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      setPasswordMessage({ type: 'error', text: 'A senha deve ter pelo menos 6 caracteres' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'As senhas não coincidem' });
      return;
    }
    setChangingPassword(true);
    try {
      await apiFetch(`/admin/users/${editingUser.id}/change-password`, {
        method: 'PUT',
        body: JSON.stringify({ newPassword }),
      });
      setPasswordMessage({ type: 'success', text: 'Senha alterada com sucesso!' });
      setNewPassword('');
      setConfirmPassword('');
      setShowChangePassword(false);
      setTimeout(() => setPasswordMessage(null), 3000);
    } catch (err) {
      setPasswordMessage({ type: 'error', text: err.message || 'Erro ao alterar senha' });
    } finally {
      setChangingPassword(false);
    }
  };

 const roleConfig = {
  Admin:        { color: '#4f46e5', bg: '#f5f3ff', border: '#ddd6fe' },
  Gestor:       { color: '#0d9488', bg: '#f0fdfa', border: '#99f6e4' },  // ← NOVO
  Atendente:    { color: '#0891b2', bg: '#ecfeff', border: '#a5f3fc' },
  Financeiro:   { color: '#059669', bg: '#ecfdf5', border: '#a7f3d0' },
  Vendedor:     { color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
  'Pós Vendas': { color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe' },
  Marketing:    { color: '#db2777', bg: '#fdf2f8', border: '#fbcfe8' },
};

  return (
    <Modal title={editingUser ? 'Editar Usuário' : 'Novo Usuário'} onClose={onClose} fecharAoClicarFora={false}
      footer={<><button className="adm-btn-cancel" onClick={onClose}>Cancelar</button><button className="adm-btn-save" onClick={handleSave}>{editingUser ? 'Salvar' : 'Criar Usuário'}</button></>}>
      <div className="adm-form-group">
        <label>Nome completo *</label>
        <input type="text" value={form.name} onChange={e => setField('name', e.target.value)} className={errors.name ? 'input-error' : ''} placeholder="Nome completo" />
        {errors.name && <span className="adm-error">{errors.name}</span>}
      </div>
      <div className="adm-form-group">
        <label>E-mail *</label>
        <input type="email" autoComplete="off" value={form.email} onChange={e => setField('email', e.target.value)} className={errors.email ? 'input-error' : ''} placeholder="email@empresa.com" />
        {errors.email && <span className="adm-error">{errors.email}</span>}
      </div>
      <div className="adm-form-group">
        <label>CPF</label>
        <input type="text" value={form.cpf} onChange={e => setField('cpf', e.target.value)} placeholder="000.000.000-00" />
      </div>

      {/* Campo de senha APENAS na criação */}
      {!editingUser && (
        <div className="adm-form-group">
          <label>Senha *</label>
          <input autoComplete="new-password" 
            type="password" 
            value={form.password} 
            onChange={e => setField('password', e.target.value)} 
            className={errors.password ? 'input-error' : ''}
            placeholder="Mínimo 6 caracteres"
          />
          {errors.password && <span className="adm-error">{errors.password}</span>}
        </div>
      )}

      {/* Botão de alterar senha APENAS na edição */}
      {editingUser && (
        <div className="adm-form-group">
          <button 
            type="button" 
            className="adm-btn-change-password"
            onClick={() => setShowChangePassword(!showChangePassword)}
          >
            <Lock size={14} /> {showChangePassword ? 'Cancelar alteração de senha' : 'Alterar senha'}
          </button>
          {showChangePassword && (
            <div className="adm-change-password-area">
              <div className="adm-form-group">
                <label>Nova senha (mínimo 6 caracteres)</label>
                <input autoComplete="new-password" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Digite a nova senha" />
              </div>
              <div className="adm-form-group">
                <label>Confirmar nova senha</label>
                <input autoComplete="new-password" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Confirme a nova senha" />
              </div>
              <button className="adm-btn-save" onClick={handleChangePassword} disabled={changingPassword} style={{ marginTop: 8 }}>
                {changingPassword ? 'Alterando...' : 'Salvar nova senha'}
              </button>
              {passwordMessage && (
                <div className={`adm-password-msg ${passwordMessage.type}`}>
                  {passwordMessage.text}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="adm-form-group">
        <label>Cargos <span style={{ fontWeight: 400, color: '#94a3b8' }}>(selecione um ou mais)</span></label>
        <div className="adm-roles-grid">
          {ROLES.map(r => {
            const cfg = roleConfig[r] || { color: '#64748b', bg: '#f1f5f9', border: '#e2e8f0' };
            const active = form.roles.includes(r);
            return (
              <button key={r} type="button" className={`adm-role-chip ${active ? 'active' : ''}`}
                style={active ? { background: cfg.bg, borderColor: cfg.color, color: cfg.color } : {}}
                onClick={() => toggleRole(r)}>
                {active && <Check size={12} />} {r}
              </button>
            );
          })}
        </div>
        <span className="adm-field-hint">Escolha o setor de cada cargo: as telas padrão já vêm marcadas, e você ajusta depois em Permissões.</span>
        {form.roles.some(r => Object.keys(TEMPLATES[canonCargo(r)] || {}).length > 0) && (
          <div className="adm-setores">
            <div className="adm-setores-titulo">Setor de cada cargo</div>
            {form.roles.map(r => {
              const cargo = canonCargo(r);
              const opcoes = Object.keys(TEMPLATES[cargo] || {});
              if (!opcoes.length) return null;
              return (
                <div key={r} className="adm-setor-linha">
                  <span className="adm-setor-cargo">{r}</span>
                  {opcoes.map(s => {
                    const ativo = (form.setores[cargo] || []).includes(s);
                    return (
                      <button key={s} type="button" className={`adm-setor-chip ${ativo ? 'active' : ''}`} onClick={() => toggleSetor(cargo, s)}>
                        {ativo && <Check size={12} />} {s}
                      </button>
                    );
                  })}
                </div>
              );
            })}
            {errors.setores && <span className="adm-error">{errors.setores}</span>}
          </div>
        )}
      </div>

      <div className="adm-form-group">
        <label>Status</label>
        <select value={form.status} onChange={e => setField('status', e.target.value)}>
          <option value="ativo">Ativo</option>
          <option value="inativo">Inativo</option>
        </select>
      </div>

      <div className="adm-perm-preview">
        <div className="adm-perm-preview-title"><Shield size={13} /> Acesso combinado para: <strong>{form.roles.join(' + ')}</strong></div>
        <div className="adm-perm-tags">
          {form.permissions.map(p => {
            const r = rotuloPerm(p);
            return r ? <span key={p} className="adm-perm-tag"><r.icon size={11} /> {r.nome}</span> : null;
          })}
        </div>
        <span className="adm-perm-preview-hint">Você pode ajustar as permissões individualmente após criar.</span>
      </div>
    </Modal>
  );
}

// =============================================================================
// 7. Aba de Modelos de Documentos (integrada com API)
// =============================================================================
const MODELO_CATS = [
  { id: 'autorizacao',   label: 'Autorização',          Icon: FileCheck,  color: 'blue'   },
  { id: 'contrato',      label: 'Contrato',             Icon: ScrollText, color: 'purple' },
  { id: 'cancelamento',  label: 'Termo de Cancelamento',Icon: FileX,      color: 'red'    },
  { id: 'carteirinha',   label: 'Modelo de Carteirinha',Icon: CardIcon,   color: 'green'  },
];

// =============================================================================
// 7.1 MODAL DE ENVIO PARA ASSINATURA (integrado com API unificada)
// =============================================================================
function EnviarAssinaturaModal({ modelo, onClose, showToast }) {
  const [apiToken, setApiToken] = useState(localStorage.getItem('clicksign_token') || '');
  const [signers, setSigners] = useState([]);
  const [signerName, setSignerName] = useState('');
  const [signerEmail, setSignerEmail] = useState('');
  const [signerRole, setSignerRole] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [saveToken, setSaveToken] = useState(true);

  // Validação simples de email
  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };
const addSigner = () => {
  const trimmedName = signerName.trim();
  const trimmedEmail = signerEmail.trim();
  const trimmedRole = signerRole.trim();

  if (!trimmedName) {
    showToast('Preencha o nome do signatário');
    return;
  }
  if (trimmedName.length < 3) {
    showToast('Nome muito curto (mínimo 3 caracteres)');
    return;
  }
  // Remove caracteres especiais (mantém letras, acentos, espaços)
  const cleanName = trimmedName.replace(/[^a-zA-ZÀ-ÿ\s]/g, '');
  if (cleanName.length === 0) {
    showToast('Nome contém caracteres inválidos. Use apenas letras e espaços.');
    return;
  }
  if (!trimmedEmail) {
    showToast('Preencha o email do signatário');
    return;
  }
  if (!isValidEmail(trimmedEmail)) {
    showToast('Email inválido. Exemplo: nome@dominio.com');
    return;
  }
  if (signers.some(s => s.email === trimmedEmail)) {
    showToast('Este email já foi adicionado');
    return;
  }
  setSigners([...signers, { 
    name: cleanName, 
    email: trimmedEmail, 
    role: trimmedRole || 'Signatário' 
  }]);
  setSignerName('');
  setSignerEmail('');
  setSignerRole('');
};

  const removeSigner = (index) => {
    setSigners(signers.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!apiToken || !apiToken.trim()) {
      showToast('Informe o Access Token da Clicksign');
      return;
    }
    if (signers.length === 0) {
      showToast('Adicione pelo menos um signatário');
      return;
    }
    if (saveToken) {
      localStorage.setItem('clicksign_token', apiToken.trim());
    }

    setLoading(true);
    setResult(null);

    try {
      // 1. Buscar o arquivo PDF do modelo (via URL pública)
      console.log('📄 Baixando PDF do modelo:', modelo.url);
      const pdfResponse = await fetch(modelo.url);
      if (!pdfResponse.ok) throw new Error('Não foi possível baixar o PDF do modelo');
      const pdfBlob = await pdfResponse.blob();

      // 2. Converter para base64
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(pdfBlob);
      });
      console.log('📄 PDF convertido para base64 (tamanho aproximado):', (base64.length / 1024).toFixed(2), 'KB');

      // 3. Preparar payload
      const payload = {
        apiToken: apiToken.trim(),
        name: modelo.nome,
        filename: modelo.arquivo,
        content_base64: base64,
        signers: signers.map(s => ({ 
          name: s.name, 
          email: s.email, 
          role: s.role || 'Signatário' 
        }))
      };
      console.log('📤 Payload a ser enviado:', {
        ...payload,
        content_base64: payload.content_base64.substring(0, 100) + '...' // log parcial
      });
      console.log('👥 Signatários:', payload.signers);

      // 4. Chamar a rota unificada do backend admin
      const data = await apiFetch('/clicksign/create-new-envelope', {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      console.log('✅ Resposta do backend:', data);
      setResult(data);
      showToast('Envelope criado com sucesso!');
    } catch (err) {
      console.error('❌ Erro no handleSubmit:', err);
      showToast(err.message || 'Erro ao criar envelope');
      setResult({ error: err.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title={`Enviar para Assinatura: ${modelo.nome}`} onClose={onClose} wide>
      {!result ? (
        <>
          <div className="adm-form-group">
            <label>Access Token Clicksign</label>
            <input
              type="text"
              value={apiToken}
              onChange={e => setApiToken(e.target.value)}
              placeholder="Cole seu token aqui"
            />
            <label style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={saveToken} onChange={e => setSaveToken(e.target.checked)} />
              Salvar token no navegador
            </label>
          </div>

          <div className="adm-form-group">
            <label>Signatários</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input type="text" placeholder="Nome" value={signerName} onChange={e => setSignerName(e.target.value)} style={{ flex: 2 }} />
              <input type="email" placeholder="Email" value={signerEmail} onChange={e => setSignerEmail(e.target.value)} style={{ flex: 2 }} />
              <input type="text" placeholder="Papel (opcional)" value={signerRole} onChange={e => setSignerRole(e.target.value)} style={{ flex: 1 }} />
              <button type="button" onClick={addSigner}>+ Adicionar</button>
            </div>
            <div className="signatories-list" style={{ marginTop: 12, maxHeight: 200, overflow: 'auto' }}>
              {signers.map((s, idx) => (
                <div key={idx} className="signatory-item" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 8, borderBottom: '1px solid #eee' }}>
                  <div><strong>{s.name}</strong> ({s.email}) - {s.role}</div>
                  <button className="modelo-btn del" onClick={() => removeSigner(idx)}><Trash2 size={14} /></button>
                </div>
              ))}
              {signers.length === 0 && <div style={{ color: '#999', textAlign: 'center' }}>Nenhum signatário adicionado</div>}
            </div>
          </div>

          <div className="adm-modal-footer">
            <button className="adm-btn-cancel" onClick={onClose}>Cancelar</button>
            <button className="adm-btn-save" onClick={handleSubmit} disabled={loading}>
              {loading ? 'Enviando...' : '🚀 Enviar para Assinatura'}
            </button>
          </div>
        </>
      ) : (
        <div>
          {result.error ? (
            <div className="status error" style={{ display: 'block' }}>
              <strong>Erro:</strong> {result.error}
            </div>
          ) : (
            <div className="status success" style={{ display: 'block' }}>
              <strong>✅ Envelope criado com sucesso!</strong><br />
              <strong>ID:</strong> {result.envelope_id}<br />
              <strong>Status:</strong> {result.envelope_sent ? 'Enviado' : 'Rascunho'}<br />
              <strong>Signatários adicionados:</strong> {result.signers_created}<br />
              {result.signers && result.signers.filter(s => s.success).map((s, i) => (
                <div key={i} style={{ marginTop: 8, padding: 8, background: '#f0f0f0', borderRadius: 6 }}>
                  <strong>{s.name}</strong> ({s.email})<br />
                  <a href={s.sign_url} target="_blank" rel="noopener noreferrer">🔗 Link para assinatura</a>
                </div>
              ))}
              <div style={{ marginTop: 16 }}>
                <button onClick={() => window.open(result.manage_url, '_blank')} className="adm-btn-save" style={{ marginRight: 8 }}>
                  Abrir no Painel Clicksign
                </button>
                <button onClick={onClose} className="adm-btn-cancel">Fechar</button>
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

function ModelosTab({ showToast }) {
  const [modelos, setModelos] = useState([]);
  const [enviandoModelo, setEnviandoModelo] = useState(null);
  const [catFilter, setCatFilter] = useState('todos');
  const [search, setSearch] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [newMod, setNewMod] = useState({ nome: '', categoria: 'contrato' });
  const [loading, setLoading] = useState(true);
  const fileRef = useRef();
  const googleCredentialRef = useRef();
  const [enviandoGoogle, setEnviandoGoogle] = useState(false);

  const loadModelos = async () => {
    try {
      const data = await apiFetch('/admin/modelos');
      setModelos(data);
    } catch (err) {
      console.error('Erro ao carregar modelos:', err);
      showToast('Erro ao carregar modelos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadModelos();
  }, []);

  const filtered = modelos.filter(m => {
    const matchCat = catFilter === 'todos' || m.categoria === catFilter;
    const matchSearch = m.nome.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const handleGoogleCredential = async () => {
    const arquivo = googleCredentialRef.current?.files?.[0];
    if (!arquivo) return showToast('Selecione o arquivo JSON');
    setEnviandoGoogle(true);
    const fd = new FormData();
    fd.append('arquivo', arquivo);
    try {
      const r = await fetch(`${API_BASE}/admin/google-credential`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: fd
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Falha no upload');
      showToast('Credencial Google enviada com sucesso');
      googleCredentialRef.current.value = '';
    } catch (e) {
      showToast(e.message);
    } finally {
      setEnviandoGoogle(false);
    }
  };

  const handleUpload = async () => {
    if (!newMod.nome) return;
    const formData = new FormData();
    formData.append('nome', newMod.nome);
    formData.append('categoria', newMod.categoria);
    if (fileRef.current?.files[0]) {
      formData.append('arquivo', fileRef.current.files[0]);
    }
    try {
      const result = await fetch(`${API_BASE}/admin/modelos`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: formData,
      });
      if (!result.ok) throw new Error('Erro ao criar modelo');
      await loadModelos();
      setNewMod({ nome: '', categoria: 'contrato' });
      setShowUpload(false);
      showToast('Modelo adicionado com sucesso!');
    } catch (err) {
      console.error(err);
      showToast('Erro ao criar modelo');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remover este modelo?')) return;
    try {
      await apiFetch(`/admin/modelos/${id}`, { method: 'DELETE' });
      await loadModelos();
      showToast('Modelo removido.');
    } catch (err) {
      console.error(err);
      showToast('Erro ao remover modelo');
    }
  };

  const getCatConfig = (cat) => MODELO_CATS.find(c => c.id === cat) || MODELO_CATS[0];

  if (loading) return <div style={{ textAlign: 'center', padding: 40 }}>Carregando modelos...</div>;

  return (
    <div className="modelos-tab">
      {/* Modal de envio para assinatura */}
      {enviandoModelo && (
        <EnviarAssinaturaModal
          modelo={enviandoModelo}
          onClose={() => setEnviandoModelo(null)}
          showToast={showToast}
        />
      )}

      <div className="modelos-header">
        <div className="modelos-search">
          <Search size={14} />
          <input type="text" placeholder="Buscar modelo..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <button className="adm-btn-new" onClick={() => setShowUpload(true)}>
          <FilePlus size={15} /> Novo Modelo
        </button>
        <input
          ref={googleCredentialRef}
          type="file"
          accept=".json,application/json"
          style={{ display: 'none' }}
          onChange={handleGoogleCredential}
        />
        <button
          className="adm-btn-new"
          type="button"
          disabled={enviandoGoogle}
          onClick={() => googleCredentialRef.current?.click()}
        >
          {enviandoGoogle ? 'Enviando...' : 'Enviar credencial Google'}
        </button>
      </div>

      <div className="modelos-cats">
        <button className={`modelos-cat-btn ${catFilter === 'todos' ? 'active' : ''}`} onClick={() => setCatFilter('todos')}>
          <FileText size={14} /> Todos <span>{modelos.length}</span>
        </button>
        {MODELO_CATS.map(c => (
          <button key={c.id} className={`modelos-cat-btn cat-${c.color} ${catFilter === c.id ? 'active-' + c.color : ''}`} onClick={() => setCatFilter(c.id)}>
            <c.Icon size={14} /> {c.label} <span>{modelos.filter(m => m.categoria === c.id).length}</span>
          </button>
        ))}
      </div>

      {showUpload && (
        <div className="modelos-upload-card">
          <h4><FilePlus size={15} /> Novo Modelo</h4>
          <div className="modelos-upload-fields">
            <div className="adm-form-group" style={{ flex: 2 }}>
              <label>Nome do modelo *</label>
              <input type="text" value={newMod.nome} onChange={e => setNewMod(p => ({ ...p, nome: e.target.value }))} placeholder="Ex: Contrato Padrão 2026" />
            </div>
            <div className="adm-form-group" style={{ flex: 1 }}>
              <label>Categoria</label>
              <select value={newMod.categoria} onChange={e => setNewMod(p => ({ ...p, categoria: e.target.value }))}>
                {MODELO_CATS.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            <div className="adm-form-group" style={{ flex: 1 }}>
              <label>Arquivo</label>
              <input type="file" ref={fileRef} style={{ fontSize: 13 }} />
            </div>
          </div>
          <div className="modelos-upload-actions">
            <button className="adm-btn-cancel" onClick={() => setShowUpload(false)}>Cancelar</button>
            <button className="adm-btn-save" onClick={handleUpload}><Check size={14} /> Salvar Modelo</button>
          </div>
        </div>
      )}

      <div className="modelos-grid">
        {filtered.map(m => {
          const cfg = getCatConfig(m.categoria);
          return (
            <div key={m.id} className={`modelo-card modelo-card-${cfg.color}`}>
              <div className="modelo-card-top">
                <div className={`modelo-icon modelo-icon-${cfg.color}`}><cfg.Icon size={22} /></div>
                <div className="modelo-info">
                  <div className="modelo-nome">{m.nome}</div>
                  <div className="modelo-cat">{cfg.label}</div>
                </div>
              </div>
              <div className="modelo-meta">
                <span>📁 {m.arquivo}</span>
                <span>💾 {m.tamanho}</span>
              </div>
              <div className="modelo-meta">
                <span>👤 {m.autor}</span>
                <span>🗓 {m.atualizado}</span>
              </div>
              <div className="modelo-actions">
                <button className="modelo-btn view" onClick={() => window.open(m.url, '_blank')}><Eye size={14} /> Ver</button>
                <button className="modelo-btn download" onClick={() => window.open(m.url, '_blank')}><Download size={14} /> Baixar</button>
                <button 
                  className="modelo-btn" 
                  style={{ background: '#4f46e5', color: 'white' }}
                  onClick={() => setEnviandoModelo(m)}
                >
                  <Send size={14} /> Enviar para Assinatura
                </button>
                <button className="modelo-btn del" onClick={() => handleDelete(m.id)}><Trash2 size={14} /></button>
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="modelos-empty"><FileText size={40} /><p>Nenhum modelo encontrado</p></div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// 8. COMPONENTE PRINCIPAL (Administracao) – totalmente integrado
// =============================================================================
export default function Administracao() {
  const [mainTab, setMainTab] = useState('usuarios');
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('Todos');
  const [modal, setModal] = useState(null); // 'new', 'edit', 'perms', 'hist'
  const [selectedUser, setSelectedUser] = useState(null);
  const [toast, setToast] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(true);

  const showToast = msg => setToast(msg);

  // Carregar lista de usuários
  const loadUsers = async () => {
    try {
      const data = await apiFetch('/admin/users');
      setUsers(data);
      // devolve a foto que veio do servidor (a lista pode ter sido remontada sem ela)
      setUsers(prev => prev.map(u => {
        const original = (Array.isArray(data) ? data : []).find(d => d.id === u.id);
        return original && original.avatar_url ? { ...u, avatar_url: original.avatar_url } : u;
      }));
    } catch (err) {
      console.error('Erro ao carregar usuários:', err);
      showToast('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  // Criar ou editar usuário
  const handleSaveUser = async (form) => {
  try {
    if (selectedUser) {
      // Editar
      const updated = await apiFetch(`/admin/users/${selectedUser.id}`, {
        method: 'PUT',
        body: JSON.stringify(form),
      });
      setUsers(prev => prev.map(u => u.id === updated.id ? { ...u, ...updated } : u));
      showToast('Usuário atualizado!');
    } else {
      // Criar
      const created = await apiFetch('/admin/users', {
        method: 'POST',
        body: JSON.stringify(form),
      });
      setUsers(prev => [...prev, created]);
      if (created.tempPassword) {
        alert(`Usuário criado! Senha temporária: ${created.tempPassword}\nRecomende que o usuário altere no primeiro acesso.`);
      } else if (form.password) {
        showToast('Usuário criado com a senha fornecida!');
      } else {
        showToast('Usuário criado!');
      }
    }
    setModal(null);
    setSelectedUser(null);
  } catch (err) {
    console.error(err);
    showToast(err.message || 'Erro ao salvar usuário');
  }
};

  // Salvar permissões
  const handleSavePerms = async (userId, permissions) => {
    try {
      const updated = await apiFetch(`/admin/users/${userId}/permissions`, {
        method: 'PUT',
        body: JSON.stringify({ permissions }),
      });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, permissions: updated.permissions } : u));
      showToast('Permissões salvas!');
      setModal(null);
      setSelectedUser(null);
    } catch (err) {
      console.error(err);
      showToast('Erro ao salvar permissões');
    }
  };

  // Excluir usuário
  const handleDelete = async (id) => {
    if (!window.confirm('Deseja remover este usuário?')) return;
    try {
      await apiFetch(`/admin/users/${id}`, { method: 'DELETE' });
      setUsers(prev => prev.filter(u => u.id !== id));
      showToast('Usuário removido.');
    } catch (err) {
      console.error(err);
      showToast('Erro ao remover usuário');
    }
  };

  const filteredUsers = users.filter(u => {
    const matchSearch = u.name?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase());
    const matchRole = filterRole === 'Todos' || (u.roles && u.roles.includes(filterRole));
    return matchSearch && matchRole;
  });

  const stats = [
    { label: 'Total', value: users.length, color: 'blue' },
    { label: 'Ativos', value: users.filter(u => u.status === 'ativo').length, color: 'green' },
    { label: 'Inativos', value: users.filter(u => u.status === 'inativo').length, color: 'blue' },
    { label: 'Admins', value: users.filter(u => u.roles?.includes('Admin')).length, color: 'green' },
  ];

  const USUARIOS_POR_PAGINA = 6;
  const [paginaUsuarios, setPaginaUsuarios] = useState(1);
  useEffect(() => { setPaginaUsuarios(1); }, [filterRole, filteredUsers.length]);
  const totalPaginasUsuarios = Math.max(1, Math.ceil(filteredUsers.length / USUARIOS_POR_PAGINA));
  const paginaUsuariosAtual = Math.min(paginaUsuarios, totalPaginasUsuarios);
  const usuariosDaPagina = filteredUsers.slice((paginaUsuariosAtual - 1) * USUARIOS_POR_PAGINA, paginaUsuariosAtual * USUARIOS_POR_PAGINA);

  if (loading) return <div className="adm-page" style={{ textAlign: 'center', padding: 40 }}>Carregando...</div>;

  return (
    <div className="adm-page">
      {toast && <Toast msg={toast} onClose={() => setToast(null)} />}

      {/* Modais */}
      {(modal === 'new' || modal === 'edit') && (
        <UserModal editingUser={modal === 'edit' ? selectedUser : null} onClose={() => { setModal(null); setSelectedUser(null); }} onSave={handleSaveUser} />
      )}
      {modal === 'perms' && selectedUser && (
        <PermissoesModal user={selectedUser} onClose={() => { setModal(null); setSelectedUser(null); }} onSave={handleSavePerms} />
      )}
      {modal === 'hist' && selectedUser && (
        <HistoricoModal user={selectedUser} onClose={() => { setModal(null); setSelectedUser(null); }} />
      )}

      {/* Header */}
      <div className="adm-header">
        <div>
          <h1>Administração</h1>
          <p className="adm-subtitle">Gerencie usuários, cargos, permissões e modelos de documentos</p>
        </div>
        {mainTab === 'usuarios' && (
          <button className="adm-btn-new" onClick={() => { setSelectedUser(null); setModal('new'); }}>
            <Plus size={15} /> Novo Usuário
          </button>
        )}
      </div>

      {/* Abas principais */}
      <div className="adm-main-tabs">
        <button className={`adm-main-tab ${mainTab === 'usuarios' ? 'active' : ''}`} onClick={() => setMainTab('usuarios')}>
          <Users size={16} /> Usuários e Permissões
        </button>
        <button className={`adm-main-tab ${mainTab === 'modelos' ? 'active' : ''}`} onClick={() => setMainTab('modelos')}>
          <FileText size={16} /> Modelos de Documentos
        </button>
      </div>

      {/* Aba Usuários */}
      {mainTab === 'usuarios' && (
        <>
          <div className="adm-stats">
            {stats.map(s => (
              <div key={s.label} className={`adm-stat adm-stat-${s.color}`}>
                <div className="adm-stat-val">{s.value}</div>
                <div className="adm-stat-label">{s.label}</div>
              </div>
            ))}
          </div>

          <div className="adm-filters">
            <div className="adm-search">
              <Search size={15} />
              <input name="adm-busca-usuarios" autoComplete="off" type="search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por nome ou e-mail..." />
            </div>
            <div className="adm-filter-tabs">
              {['Todos', ...ROLES].map(r => (
                <button key={r} className={`adm-tab ${filterRole === r ? 'active' : ''}`} onClick={() => setFilterRole(r)}>
                  {r} <span className="adm-tab-count">{r === 'Todos' ? users.length : users.filter(u => u.roles?.includes(r)).length}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="adm-table-wrapper">
            <table className="adm-table">
              <thead>
                <tr><th>Usuário</th><th>Cargo</th><th>CPF</th><th>Status</th><th>Módulos</th><th>Ações</th></tr>
              </thead>
              <tbody>
                {usuariosDaPagina.map((u, i) => (
                  <React.Fragment key={u.id}>
                    <tr className={i % 2 === 0 ? 'row-even' : 'row-odd'}>
                      <td>
                        <div className="adm-user-cell">
                          {u.avatar_url ? <img className="adm-avatar adm-avatar-foto" src={u.avatar_url} alt={u.name || ''} /> : <div className="adm-avatar" style={{ background: roleColor(u.roles) }}>{u.name?.charAt(0)}</div>}
                          <div>
                            <div className="adm-user-name">{u.name}</div>
                            <div className="adm-user-email">{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="adm-roles-badges">
                          {(u.roles || []).map(r => (
                            <span key={r} className={`adm-role-badge role-${r.toLowerCase().replace(/ /g, '-')}`}>{r}</span>
                          ))}
                        </div>
                        {textoSetores(u) && <div className="adm-setores-mini">{textoSetores(u)}</div>}
                      </td>
                      <td className="adm-cpf">{u.cpf}</td>
                      <td>
                        <span className={`adm-status status-${u.status}`}>
                          {u.status === 'ativo' ? <Unlock size={11} /> : <Lock size={11} />} {u.status}
                        </span>
                      </td>
                      <td>
                        <div className="adm-mods-cell">
                          <div className="adm-mods-icons">
                            {modulosComAcesso(u.permissions).slice(0, 5).map(mod => (
                              <span key={mod.id} className="adm-mod-dot" title={mod.name}><mod.icon size={12} /></span>
                            ))}
                            {modulosComAcesso(u.permissions).length > 5 && <span className="adm-mod-more">+{modulosComAcesso(u.permissions).length - 5}</span>}
                          </div>
                          <button className="adm-expand-btn" onClick={() => setExpanded(prev => ({ ...prev, [u.id]: !prev[u.id] }))}>
                            {expanded[u.id] ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                          </button>
                        </div>
                      </td>
                      <td>
                        <div className="adm-actions">
                          <button className="adm-action-btn hist" onClick={() => { setSelectedUser(u); setModal('hist'); }} title="Histórico"><History size={13} /> Histórico</button>
                          <button className="adm-action-btn perms" onClick={() => { setSelectedUser(u); setModal('perms'); }} title="Permissões"><Shield size={13} /> Permissões</button>
                          <button className="adm-action-btn edit" onClick={() => { setSelectedUser(u); setModal('edit'); }} title="Editar">Editar</button>
                          <button className="adm-action-btn del" onClick={() => handleDelete(u.id)} title="Remover">Remover</button>
                        </div>
                      </td>
                    </tr>
                    {expanded[u.id] && (
                      <tr className="adm-expanded-row">
                        <td colSpan={6}>
                          <div className="adm-expanded-content">
                            <div className="adm-expanded-title"><Shield size={13} /> Módulos acessíveis por <strong>{u.name}</strong></div>
                            <div className="adm-expanded-grid">
                              {ALL_MODULES.map(mod => {
                                const has = estadoModulo(u.permissions, mod.id) !== 'nenhum';
                                return (
                                  <div key={mod.id} className={`adm-expanded-item ${has ? 'has-access' : 'no-access'}`}>
                                    <mod.icon size={14} /> <span>{mod.name}</span>
                                    {has ? <Check size={11} className="adm-check-icon" /> : <X size={11} className="adm-x-icon" />}
                                  </div>
                                );
                              })}
                            </div>
                            <button className="adm-btn-edit-perms" onClick={() => { setSelectedUser(u); setModal('perms'); }}>
                              <Shield size={13} /> Editar permissões
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
                {filteredUsers.length === 0 && <tr><td colSpan={6} className="adm-empty">Nenhum usuário encontrado</td></tr>}
              </tbody>
            </table>
            {filteredUsers.length > USUARIOS_POR_PAGINA && (
              <div className="adm-paginacao">
                <span>Mostrando {(paginaUsuariosAtual - 1) * USUARIOS_POR_PAGINA + 1}–{Math.min(paginaUsuariosAtual * USUARIOS_POR_PAGINA, filteredUsers.length)} de {filteredUsers.length}</span>
                <div>
                  <button type="button" disabled={paginaUsuariosAtual === 1} onClick={() => setPaginaUsuarios(paginaUsuariosAtual - 1)}>‹ Anterior</button>
                  <span className="adm-paginacao-atual">Página {paginaUsuariosAtual} de {totalPaginasUsuarios}</span>
                  <button type="button" disabled={paginaUsuariosAtual === totalPaginasUsuarios} onClick={() => setPaginaUsuarios(paginaUsuariosAtual + 1)}>Próxima ›</button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Aba Modelos */}
      {mainTab === 'modelos' && <ModelosTab showToast={showToast} />}
    </div>
  );
}
